import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Clock, CheckCircle2, XCircle, DollarSign, Calendar,
  AlertTriangle, FileText, Printer, Loader, TrendingUp, Info,
  CreditCard, Wallet, ShieldCheck, User, Phone, Mail, Send, Banknote, Pencil,
} from "lucide-react";
import { format } from "date-fns";
import { cn, formatCurrency } from "@/lib/utils";
import { StatusBadge } from "@/components/ui/StatusBadge";
import PaymentModal from "@/components/loans/PaymentModal";
import type { PaymentFormData } from "@/components/loans/PaymentModal";
import { toast } from "sonner";

interface LoanDetailData {
  id: number;
  loanCode: string;
  amount: number;
  interestRate: number;
  termMonths: number;
  monthlyPayment: number;
  dueAmount: number;
  totalPaid: number;
  upfrontInterest: number;
  disbursedAmount: number;
  status: string;
  purpose: string;
  loanType: string;
  applicationDate: string;
  approvalDate: string;
  disbursementDate: string;
  maturityDate: string;
  guarantor1: string;
  guarantor2: string;
  guarantor1Phone: string;
  guarantor2Phone: string;
  rejectionReason: string;
  memberCode: string;
  memberName: string;
  memberPhone?: string;
  memberEmail?: string;
  memberId?: number;
  approvalHistory: {
    president: { approved: boolean; approvedBy: string; approvedAt: string; comment: string };
    secretary: { approved: boolean; approvedBy: string; approvedAt: string; comment: string };
    accountant: { approved: boolean; approvedBy: string; approvedAt: string; comment: string };
    approvalCount: number;
    requiredApprovals: number;
  };
  schedule?: Array<{
    id: number;
    installmentNumber: number;
    principalAmount: number;
    interestAmount: number;
    totalAmount: number;
    paidAmount: number;
    remainingBalance: number;
    dueDate: string;
    status: string;
    daysOverdue: number;
  }>;
  scheduleSummary?: {
    totalScheduled: number;
    totalPaid: number;
    remainingBalance: number;
    totalInstallments: number;
    paidInstallments: number;
    overdueInstallments: number;
    nextDueDate: string;
    nextDueAmount: number;
    monthlyPayment: number;
  };
  recentPayments: Array<{
    id: number;
    amount: number;
    paymentDate: string;
    paymentMethod: string;
    isLate: boolean;
    daysLate: number;
  }>;
  guarantors?: Array<{
    id: number;
    name: string;
    memberCode: string;
    phone: string;
    guaranteeLimit: number | null;
    status: "pending" | "approved" | "rejected";
    approvedAt: string | null;
  }>;
  guarantorSummary?: {
    required: number;
    approved: number;
    pending: number;
    rejected: number;
  };
  teacher?: {
    id: number;
    fullName: string;
    phone: string;
    email?: string;
    memberCode: string;
    firstName?: string;
    lastName?: string;
  };
  member?: {
    id: number;
    fullNames: string;
    phone: string;
    email?: string;
    memberCode: string;
  };
}

const APPROVABLE_STATUSES = ["pending", "pending_approval", "awaiting_approval", "approved_by_leaders"];

export default function AdminLoanDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"overview" | "schedule" | "payments">("overview");
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [editForm, setEditForm] = useState({ amount: 0, purpose: "", termMonths: 12, loanType: "" });

  const { data: loan, isLoading, error } = useQuery(
    ["loan-detail", id],
    async () => {
      const { data } = await api.get(`/loans/${id}`);
      return data.data as LoanDetailData;
    },
    { enabled: !!id }
  );

  const normalizeStatus = (s: string) => String(s || "").toLowerCase().replace(/\s+/g, "_");

  const repaymentProgress = useMemo(() => {
    if (!loan) return 0;
    const total = loan.amount;
    const paid = loan.totalPaid || 0;
    return Math.min(100, Math.round((paid / total) * 100));
  }, [loan]);

  const nextDue = useMemo(() => {
    if (!loan?.schedule) return null;
    return loan.schedule.find(s => s.status === "pending" || s.status === "overdue");
  }, [loan]);

  const statusNorm = loan ? normalizeStatus(loan.status) : "";
  const isApprovable = APPROVABLE_STATUSES.includes(statusNorm);
  const isFullyApproved = (loan?.approvalHistory?.approvalCount || 0) >= 3;
  const canDisburse = isFullyApproved && ["approved", "pending", "pending_approval", "approved_by_leaders"].includes(statusNorm);
  const canRecordPayment = ["active", "disbursed", "overdue", "approved"].includes(statusNorm);
  const cancelableStatuses = ["draft", "pending", "pending_approval", "pending_guarantor_approval"];
  const isCancelable = loan ? cancelableStatuses.includes(normalizeStatus(loan.status)) : false;

  // ─── Mutations ────────────────────────────────────────────────────────────
  const approveMutation = useMutation(
    async () => {
      return api.post(`/loans/${loan?.id}/approve`, {
        status: "Approved",
        notes: "Fully approved by admin",
      });
    },
    {
      onSuccess: () => {
        toast.success("Loan approved successfully");
        queryClient.invalidateQueries(["loan-detail", id]);
        queryClient.invalidateQueries({ queryKey: ["loans"], exact: false });
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to approve loan");
      },
    }
  );

  const rejectMutation = useMutation(
    async (reason: string) => {
      return api.post(`/loans/${loan?.id}/approve`, {
        status: "Rejected",
        notes: reason,
      });
    },
    {
      onSuccess: () => {
        toast.success("Loan rejected");
        queryClient.invalidateQueries(["loan-detail", id]);
        queryClient.invalidateQueries({ queryKey: ["loans"], exact: false });
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to reject loan");
      },
    }
  );

  const disburseMutation = useMutation(
    async () => {
      return api.patch(`/loans/${loan?.id}/disburse`);
    },
    {
      onSuccess: () => {
        toast.success("Loan disbursed successfully");
        queryClient.invalidateQueries(["loan-detail", id]);
        queryClient.invalidateQueries({ queryKey: ["loans"], exact: false });
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to disburse loan");
      },
    }
  );

  const recordPaymentMutation = useMutation(
    async (payload: PaymentFormData) => {
      return api.post(`/loans/${loan?.id}/payments`, payload);
    },
    {
      onSuccess: () => {
        toast.success("Payment recorded successfully");
        setShowRecordPayment(false);
        queryClient.invalidateQueries(["loan-detail", id]);
        queryClient.invalidateQueries({ queryKey: ["loans"], exact: false });
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to record payment");
      },
    }
  );

  const cancelMutation = useMutation(
    async () => {
      const { data } = await api.patch(`/loans/${loan?.id}/cancel`);
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Loan cancelled successfully");
        setShowCancelConfirm(false);
        queryClient.invalidateQueries(["loan-detail", id]);
        queryClient.invalidateQueries({ queryKey: ["loans"], exact: false });
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to cancel loan");
      },
    }
  );

  const updateMutation = useMutation(
    async (payload: any) => {
      const { data } = await api.put(`/loans/${loan?.id}`, payload);
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Loan updated successfully");
        setShowEditDialog(false);
        queryClient.invalidateQueries(["loan-detail", id]);
        queryClient.invalidateQueries({ queryKey: ["loans"], exact: false });
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to update loan");
      },
    }
  );

  // ─── Loading / Error states ───────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (error || !loan) {
    return (
      <div className="sm:px-4 px-3 pt-3 pb-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center px-4">
          <div className="bg-white border border-gray-200 rounded-xl p-8 max-w-md w-full shadow-sm">
            <div className="flex justify-center mb-4">
              <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center">
                <FileText className="h-7 w-7 text-gray-400" />
              </div>
            </div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Loan not found</h2>
            <p className="text-sm text-gray-500 mb-6">
              The loan you are looking for does not exist or has been removed.
            </p>
            <Button onClick={() => navigate("/loans")} className="bg-[#009A44] hover:bg-[#008238] text-white gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Loans
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Status timeline ──────────────────────────────────────────────────────
  const hasGuarantors = loan.guarantors && loan.guarantors.length > 0;
  const allGuarantorsApproved = hasGuarantors && loan.guarantorSummary?.approved === loan.guarantors!.length;

  const buildStatusTimeline = () => {
    const steps = [
      { label: "Applied", date: loan.applicationDate, done: true },
    ];
    if (hasGuarantors) {
      steps.push({
        label: "Awaiting Guarantor Approval",
        date: null,
        done: allGuarantorsApproved || ["approved", "active", "completed", "fully_repaid", "disbursed"].includes(normalizeStatus(loan.status)),
      });
    }
    steps.push(
      { label: "Approved", date: loan.approvalDate, done: ["approved", "active", "completed", "fully_repaid", "disbursed"].includes(normalizeStatus(loan.status)) },
      { label: "Disbursed", date: loan.disbursementDate, done: ["active", "completed", "fully_repaid"].includes(normalizeStatus(loan.status)) },
      { label: "Completed", date: null, done: normalizeStatus(loan.status) === "completed" || normalizeStatus(loan.status) === "fully_repaid" },
    );
    return steps;
  };
  const statusTimeline = buildStatusTimeline();

  const memberName = loan.memberName || loan.member?.fullNames || loan.teacher?.fullName || `${loan.teacher?.firstName || ""} ${loan.teacher?.lastName || ""}`.trim() || "N/A";
  const memberPhone = loan.memberPhone || loan.member?.phone || loan.teacher?.phone || "N/A";
  const memberEmail = loan.memberEmail || loan.member?.email || loan.teacher?.email || "N/A";

  return (
    <div className="sm:px-4 px-3 pt-3 pb-8">
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/loans")}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Loans
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Loan Details</h1>
            <p className="text-sm text-slate-500">
              {loan.loanCode || `LN-${String(loan.id).padStart(4, "0")}`} &middot; Applied {format(new Date(loan.applicationDate), "MMM dd, yyyy HH:mm")}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={loan.status} />

            {isCancelable && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => {
                    setEditForm({ amount: loan.amount, purpose: loan.purpose, termMonths: loan.termMonths, loanType: loan.loanType });
                    setShowEditDialog(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="gap-1.5"
                  onClick={() => setShowCancelConfirm(true)}
                >
                  <XCircle className="h-3.5 w-3.5" />
                  Cancel
                </Button>
              </>
            )}

            {isApprovable && user?.isAdmin && (
              <>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white gap-1.5"
                  onClick={() => approveMutation.mutate()}
                  disabled={approveMutation.isLoading}
                >
                  {approveMutation.isLoading ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="gap-1.5"
                  onClick={() => {
                    const reason = prompt("Rejection reason:");
                    if (reason) rejectMutation.mutate(reason);
                  }}
                  disabled={rejectMutation.isLoading}
                >
                  {rejectMutation.isLoading ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                  Reject
                </Button>
              </>
            )}

            {canDisburse && user?.isAdmin && (
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
                onClick={() => disburseMutation.mutate()}
                disabled={disburseMutation.isLoading}
              >
                {disburseMutation.isLoading ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Banknote className="h-3.5 w-3.5" />}
                Disburse
              </Button>
            )}

            {canRecordPayment && (
              <Button
                size="sm"
                className="bg-[#009A44] hover:bg-[#008238] text-white gap-1.5"
                onClick={() => setShowRecordPayment(true)}
              >
                <DollarSign className="h-3.5 w-3.5" />
                Record Payment
              </Button>
            )}

            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => window.print()}>
              <Printer className="h-3.5 w-3.5" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* ─── Status Timeline ─────────────────────────────────────────────── */}
      <Card className="mb-6">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            {statusTimeline.map((step, i) => (
              <div key={step.label} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div className={cn(
                    "h-8 w-8 rounded-full flex items-center justify-center border-2",
                    step.done ? "bg-green-500 border-green-500 text-white" : "bg-gray-100 border-gray-300 text-gray-400"
                  )}>
                    {step.done ? <CheckCircle2 className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                  </div>
                  <span className="text-[10px] mt-1 font-medium text-gray-600">{step.label}</span>
                  {step.date && (
                    <span className="text-[9px] text-gray-400">
                      {format(new Date(step.date), "MMM dd HH:mm")}
                    </span>
                  )}
                </div>
                {i < statusTimeline.length - 1 && (
                  <div className={cn(
                    "flex-1 h-0.5 mx-1",
                    step.done && statusTimeline[i + 1].done ? "bg-green-500" : "bg-gray-200"
                  )} />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ─── KPI Cards ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="h-4 w-4 text-green-600" />
              <span className="text-xs text-gray-500">Loan Amount</span>
            </div>
            <p className="text-lg font-bold text-gray-900">{formatCurrency(loan.amount)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <span className="text-xs text-gray-500">Repaid</span>
            </div>
            <p className="text-lg font-bold text-blue-600">{formatCurrency(loan.totalPaid || 0)}</p>
            <Progress value={repaymentProgress} className="h-1.5 mt-1" />
            <p className="text-[10px] text-gray-400 mt-1">{repaymentProgress}% complete</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="h-4 w-4 text-orange-600" />
              <span className="text-xs text-gray-500">Outstanding</span>
            </div>
            <p className="text-lg font-bold text-orange-600">{formatCurrency(loan.dueAmount || 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-purple-600" />
              <span className="text-xs text-gray-500">Next Payment</span>
            </div>
            {nextDue ? (
              <>
                <p className="text-lg font-bold text-gray-900">{formatCurrency(nextDue.totalAmount)}</p>
                <p className="text-[10px] text-gray-400">
                  Due {format(new Date(nextDue.dueDate), "MMM dd, yyyy")}
                </p>
              </>
            ) : (
              <p className="text-lg font-bold text-gray-400">-</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── Tabs ────────────────────────────────────────────────────────── */}
      <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg">
        {(["overview", "schedule", "payments"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              "flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors",
              activeTab === tab ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            )}
          >
            {tab === "overview" ? "Overview" : tab === "schedule" ? "Repayment Schedule" : "Payments"}
          </button>
        ))}
      </div>

      {/* ─── Overview Tab ────────────────────────────────────────────────── */}
      {activeTab === "overview" && (
        <div className="space-y-4">
          {/* Loan Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-green-600" />
                Loan Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Loan ID:</span> <span className="font-medium ml-1 font-mono">{loan.loanCode || `LN-${String(loan.id).padStart(4, "0")}`}</span></div>
                <div><span className="text-gray-500">Loan Type:</span> <span className="font-medium ml-1">{loan.loanType || "Monthly"}</span></div>
                <div><span className="text-gray-500">Interest Rate:</span> <span className="font-medium ml-1">{loan.interestRate}%</span></div>
                <div><span className="text-gray-500">Term:</span> <span className="font-medium ml-1">{loan.termMonths} months</span></div>
                <div><span className="text-gray-500">Monthly Payment:</span> <span className="font-medium ml-1">{formatCurrency(loan.monthlyPayment)}</span></div>
                <div><span className="text-gray-500">Purpose:</span> <span className="font-medium ml-1">{loan.purpose || "General"}</span></div>
              </div>

              {loan.upfrontInterest > 0 && (
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                  <p className="text-xs font-medium text-blue-700 mb-1">Disbursement Summary</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-blue-600">
                    <span>Gross Amount: {formatCurrency(loan.amount)}</span>
                    <span>Interest Deducted: {formatCurrency(loan.upfrontInterest)}</span>
                    <span className="font-semibold">Net Disbursed: {formatCurrency(loan.disbursedAmount)}</span>
                    <span>Maturity: {loan.maturityDate ? format(new Date(loan.maturityDate), "MMM dd, yyyy") : "N/A"}</span>
                  </div>
                </div>
              )}

              {loan.rejectionReason && (
                <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                  <p className="text-xs font-medium text-red-700 mb-1">Rejection Reason</p>
                  <p className="text-sm text-red-600">{loan.rejectionReason}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Member Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <User className="h-4 w-4 text-green-600" />
                Member Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Name:</span> <span className="font-medium ml-1">{memberName}</span></div>
                <div><span className="text-gray-500">Member ID:</span> <span className="font-medium ml-1 font-mono">{loan.memberCode || loan.member?.memberCode || loan.teacher?.memberCode || "N/A"}</span></div>
                <div className="flex items-center gap-1">
                  <Phone className="h-3 w-3 text-gray-400" />
                  <span className="text-gray-500">Phone:</span> <span className="font-medium ml-1">{memberPhone}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Mail className="h-3 w-3 text-gray-400" />
                  <span className="text-gray-500">Email:</span> <span className="font-medium ml-1">{memberEmail}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Approval Progress */}
          {loan.approvalHistory && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-green-600" />
                  Approval Progress ({loan.approvalHistory.approvalCount}/{loan.approvalHistory.requiredApprovals})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(["president", "secretary", "accountant"] as const).map((role) => {
                    const info = loan.approvalHistory[role];
                    return (
                      <div key={role} className="flex items-center gap-3">
                        <div className={cn(
                          "h-7 w-7 rounded-full flex items-center justify-center shrink-0",
                          info.approved ? "bg-green-100" : info.comment ? "bg-red-100" : "bg-gray-100"
                        )}>
                          {info.approved ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          ) : info.comment ? (
                            <XCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium capitalize">{role}</span>
                            {info.approved && (
                              <Badge variant="secondary" className="text-[9px] bg-green-100 text-green-700 border-green-200">
                                Approved
                              </Badge>
                            )}
                            {info.comment && !info.approved && (
                              <Badge variant="secondary" className="text-[9px] bg-red-100 text-red-700 border-red-200">
                                Rejected
                              </Badge>
                            )}
                          </div>
                          {info.approvedBy && (
                            <p className="text-xs text-gray-500">
                              by {info.approvedBy}
                              {info.approvedAt && ` on ${format(new Date(info.approvedAt), "MMM dd, yyyy HH:mm")}`}
                            </p>
                          )}
                          {info.comment && (
                            <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">"{info.comment}"</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Requested Guarantors */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <User className="h-4 w-4 text-green-600" />
                Requested Guarantors
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loan.guarantors && loan.guarantors.length > 0 ? (
                <div className="space-y-3">
                  {/* Summary bar */}
                  {loan.guarantorSummary && (
                    <div className={cn(
                      "rounded-lg p-2.5 border text-xs flex items-center gap-2",
                      loan.guarantorSummary.approved === loan.guarantors.length
                        ? "bg-green-50 border-green-200 text-green-700"
                        : "bg-amber-50 border-amber-200 text-amber-700"
                    )}>
                      {loan.guarantorSummary.approved === loan.guarantors.length ? (
                        <><CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> All guarantors have approved</>
                      ) : (
                        <><Clock className="h-3.5 w-3.5 shrink-0" /> {loan.guarantorSummary.approved} of {loan.guarantors.length} approved &middot; {loan.guarantorSummary.pending} pending</>
                      )}
                    </div>
                  )}

                  {/* Guarantor cards */}
                  {loan.guarantors.map((g, idx) => (
                    <div key={g.id} className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-3 py-1.5 border-b flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                          Guarantor {idx + 1}
                        </span>
                        <span className={cn(
                          "text-[10px] font-semibold px-2 py-0.5 rounded-full border",
                          g.status === "approved" && "bg-green-50 text-green-700 border-green-200",
                          g.status === "pending" && "bg-amber-50 text-amber-700 border-amber-200",
                          g.status === "rejected" && "bg-red-50 text-red-700 border-red-200",
                        )}>
                          {g.status === "approved" && "Approved"}
                          {g.status === "pending" && "Pending"}
                          {g.status === "rejected" && "Rejected"}
                        </span>
                      </div>
                      <div className="p-3 flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center shrink-0 text-base font-bold text-green-700">
                          {(g.name || "N/A").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {g.name || "N/A"}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                            {g.memberCode && (
                              <span className="font-mono">{g.memberCode}</span>
                            )}
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3 text-gray-400" />
                              {g.phone || "N/A"}
                            </span>
                          </div>
                          {g.guaranteeLimit != null && (
                            <p className="text-[11px] text-gray-400">
                              Max guarantee: {formatCurrency(g.guaranteeLimit)}
                            </p>
                          )}
                          {g.approvedAt && (
                            <p className="text-[11px] text-green-600 flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" />
                              Approved {format(new Date(g.approvedAt), "MMM dd, yyyy HH:mm")}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-3 py-2">
                  <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <Info className="h-4 w-4 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500">No guarantor requested</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── Schedule Tab ────────────────────────────────────────────────── */}
      {activeTab === "schedule" && (
        <Card>
          <CardContent className="p-0">
            {loan.schedule && loan.schedule.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">#</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Due Date</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Principal</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Interest</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Total</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Paid</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Balance</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loan.schedule.map((s) => (
                      <tr key={s.id} className={cn("border-b last:border-0", s.status === "overdue" && "bg-red-50")}>
                        <td className="py-2.5 px-4 font-mono text-gray-500">{s.installmentNumber}</td>
                        <td className="py-2.5 px-4">{format(new Date(s.dueDate), "MMM dd, yyyy")}</td>
                        <td className="py-2.5 px-4 text-right">{formatCurrency(s.principalAmount)}</td>
                        <td className="py-2.5 px-4 text-right">{formatCurrency(s.interestAmount)}</td>
                        <td className="py-2.5 px-4 text-right font-medium">{formatCurrency(s.totalAmount)}</td>
                        <td className="py-2.5 px-4 text-right text-green-600">{s.paidAmount > 0 ? formatCurrency(s.paidAmount) : "0.00"}</td>
                        <td className="py-2.5 px-4 text-right text-orange-600">{formatCurrency(s.remainingBalance)}</td>
                        <td className="py-2.5 px-4 text-center">
                          <Badge
                            variant="secondary"
                            className={cn(
                              "text-[9px] px-1.5 py-0",
                              s.status === "paid" && "bg-green-100 text-green-700",
                              s.status === "pending" && "bg-gray-100 text-gray-600",
                              s.status === "overdue" && "bg-red-100 text-red-700",
                              s.status === "partial" && "bg-amber-100 text-amber-700",
                            )}
                          >
                            {s.status}
                            {s.daysOverdue ? ` (${s.daysOverdue}d)` : ""}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 text-center text-gray-500">
                <Calendar className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Repayment schedule will appear after disbursement.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Payments Tab ────────────────────────────────────────────────── */}
      {activeTab === "payments" && (
        <Card>
          <CardContent className="p-0">
            {loan.recentPayments && loan.recentPayments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Date</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-600">Amount</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Method</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loan.recentPayments.map((p) => (
                      <tr key={p.id} className="border-b last:border-0">
                        <td className="py-2.5 px-4">{format(new Date(p.paymentDate), "MMM dd, yyyy HH:mm")}</td>
                        <td className="py-2.5 px-4 text-right font-medium text-green-600">{formatCurrency(p.amount)}</td>
                        <td className="py-2.5 px-4 text-gray-500">{p.paymentMethod || "N/A"}</td>
                        <td className="py-2.5 px-4 text-center">
                          {p.isLate ? (
                            <Badge variant="secondary" className="text-[9px] bg-amber-100 text-amber-700">
                              Late ({p.daysLate}d)
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-[9px] bg-green-100 text-green-700">
                              On Time
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 text-center text-gray-500">
                <DollarSign className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No payments recorded yet.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Record Payment Modal ───────────────────────────────────────── */}
      <PaymentModal
        open={showRecordPayment}
        onOpenChange={setShowRecordPayment}
        loan={loan}
        mode="admin"
        onSubmit={(data) => recordPaymentMutation.mutate(data)}
        isLoading={recordPaymentMutation.isLoading}
      />

      {/* Edit Loan Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-green-600" />
              Edit Loan
            </DialogTitle>
            <DialogDescription>
              Update this loan application. Changes can only be made before approval.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium">Loan Amount (FRW)</Label>
              <Input
                type="number"
                className="mt-1"
                value={editForm.amount || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, amount: parseInt(e.target.value) || 0 }))}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Term (months)</Label>
              <Select
                value={String(editForm.termMonths)}
                onValueChange={(val) => setEditForm(prev => ({ ...prev, termMonths: parseInt(val) }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[3, 6, 9, 12, 18, 24].map(m => (
                    <SelectItem key={m} value={String(m)}>{m} months</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Purpose</Label>
              <Textarea
                className="mt-1"
                rows={2}
                value={editForm.purpose}
                onChange={(e) => setEditForm(prev => ({ ...prev, purpose: e.target.value }))}
                placeholder="Loan purpose"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowEditDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-[#009A44] hover:bg-[#008238]"
                onClick={() => {
                  if (!editForm.amount || editForm.amount <= 0) {
                    toast.error("Please enter a valid amount");
                    return;
                  }
                  updateMutation.mutate({
                    amount: editForm.amount,
                    termMonths: editForm.termMonths,
                    purpose: editForm.purpose,
                  });
                }}
                disabled={updateMutation.isLoading}
              >
                {updateMutation.isLoading ? (
                  <><Loader className="h-4 w-4 animate-spin mr-2" /> Saving...</>
                ) : (
                  <>Save Changes</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Cancel Loan
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this loan application? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowCancelConfirm(false)}
            >
              Keep Loan
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isLoading}
            >
              {cancelMutation.isLoading ? (
                <><Loader className="h-4 w-4 animate-spin mr-2" /> Cancelling...</>
              ) : (
                <><XCircle className="h-4 w-4 mr-2" /> Yes, Cancel</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
